import { Head, Link, router, useForm } from "@inertiajs/react"
import Cropper from "cropperjs"
import { useCallback, useEffect, useRef, useState } from "react"
import "cropperjs/dist/cropper.css"

import { FocalPointPicker } from "@/components/focal-point-picker"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface SubjectRecord {
  id: number
  display_name: string
}

interface PhotoData {
  id: number
  caption: string
  width: number | null
  height: number | null
  exclude_from_worksheet: boolean
  focal_point_x: number
  focal_point_y: number
  image_url: string | null
  crop_rotate_url: string
}

interface Props {
  case_record: CaseRecord
  subject_record: SubjectRecord
  photo: PhotoData
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  subject_record,
  photo,
  errors,
}: Props) {
  const c = case_record
  const s = subject_record

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: c.display_name || c.case_number, href: `/cases/${c.id}` },
    { title: s.display_name, href: `/cases/${c.id}/subjects/${s.id}` },
    { title: "Photos", href: `/cases/${c.id}/subjects/${s.id}/photos` },
    {
      title: `Edit Photo #${photo.id}`,
      href: `/cases/${c.id}/subjects/${s.id}/photos/${photo.id}/edit`,
    },
  ]

  const basePath = `/cases/${c.id}/subjects/${s.id}/photos`

  const { data, setData, processing } = useForm({
    caption: photo.caption,
    exclude_from_worksheet: photo.exclude_from_worksheet,
    focal_point_x: photo.focal_point_x,
    focal_point_y: photo.focal_point_y,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch(`${basePath}/${photo.id}`, { photo: data })
  }

  // Crop/rotate state
  const imageRef = useRef<HTMLImageElement>(null)
  const cropperRef = useRef<Cropper | null>(null)
  const [cropMode, setCropMode] = useState(false)
  const [focalPointMode, setFocalPointMode] = useState(false)
  const [cropProcessing, setCropProcessing] = useState(false)

  const initCropper = useCallback(() => {
    if (!imageRef.current) return
    if (cropperRef.current) {
      cropperRef.current.destroy()
    }
    cropperRef.current = new Cropper(imageRef.current, {
      viewMode: 2,
      autoCropArea: 1,
      background: false,
      rotatable: true,
      zoomable: true,
    })
  }, [])

  const destroyCropper = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.destroy()
      cropperRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => destroyCropper()
  }, [destroyCropper])

  const handleEnterCropMode = () => {
    setFocalPointMode(false)
    setCropMode(true)
    setTimeout(initCropper, 100)
  }

  const handleEnterFocalPointMode = () => {
    destroyCropper()
    setCropMode(false)
    setFocalPointMode(true)
  }

  const handleCancelCrop = () => {
    destroyCropper()
    setCropMode(false)
  }

  const handleRotateLeft = () => {
    cropperRef.current?.rotate(-90)
  }

  const handleRotateRight = () => {
    cropperRef.current?.rotate(90)
  }

  const handleSaveCrop = () => {
    if (!cropperRef.current) return
    setCropProcessing(true)

    const cropData = cropperRef.current.getData(true)

    router.patch(
      photo.crop_rotate_url,
      {
        crop_x: Math.max(0, cropData.x),
        crop_y: Math.max(0, cropData.y),
        crop_width: cropData.width,
        crop_height: cropData.height,
        rotation: cropData.rotate,
      },
      {
        onFinish: () => {
          setCropProcessing(false)
          setCropMode(false)
          destroyCropper()
        },
      },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`Edit Photo #${photo.id} - ${s.display_name} - ${c.case_number}`}
      />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Photo #{photo.id}</h1>

        {photo.image_url && (
          <div className="mb-6">
            <div className="text-center">
              {focalPointMode ? (
                <div className="mx-auto inline-block">
                  <FocalPointPicker
                    imageUrl={photo.image_url}
                    focalPointX={data.focal_point_x}
                    focalPointY={data.focal_point_y}
                    onChange={(x, y) => {
                      setData((prev) => ({
                        ...prev,
                        focal_point_x: x,
                        focal_point_y: y,
                      }))
                    }}
                  />
                </div>
              ) : (
                <>
                  <div
                    className={cropMode ? "mx-auto max-w-3xl" : ""}
                    style={cropMode ? { maxHeight: 500 } : undefined}
                  >
                    <img
                      ref={imageRef}
                      src={photo.image_url}
                      alt={photo.caption || `Photo #${photo.id}`}
                      className={
                        cropMode
                          ? "block max-w-full"
                          : "mx-auto max-h-[300px] rounded"
                      }
                      key={cropMode ? "crop" : "view"}
                    />
                  </div>
                  {photo.width && photo.height && !cropMode && (
                    <div className="text-hpi-med-gray mt-1 text-sm">
                      {photo.width}x{photo.height}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Crop/Rotate/Focal Point controls */}
            <div className="mt-3 flex items-center justify-center gap-3">
              {!cropMode && !focalPointMode ? (
                <>
                  <button
                    type="button"
                    onClick={handleEnterCropMode}
                    className="bg-hpi-blue hover:bg-hpi-dark-blue rounded px-4 py-2 text-sm text-white"
                  >
                    Crop / Rotate
                  </button>
                  <button
                    type="button"
                    onClick={handleEnterFocalPointMode}
                    className="rounded border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50"
                  >
                    Set Focal Point
                  </button>
                </>
              ) : cropMode ? (
                <>
                  <button
                    type="button"
                    onClick={handleRotateLeft}
                    className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                    title="Rotate Left"
                  >
                    Rotate Left
                  </button>
                  <button
                    type="button"
                    onClick={handleRotateRight}
                    className="rounded border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
                    title="Rotate Right"
                  >
                    Rotate Right
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCrop}
                    disabled={cropProcessing}
                    className="bg-hpi-green hover:bg-hpi-green/90 rounded px-4 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {cropProcessing ? "Saving..." : "Apply"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelCrop}
                    className="text-hpi-active-blue text-sm hover:underline"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setFocalPointMode(false)}
                  className="text-hpi-active-blue text-sm hover:underline"
                >
                  Done Setting Focal Point
                </button>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mx-auto max-w-2xl space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">
              Caption (optional)
            </label>
            <input
              type="text"
              value={data.caption}
              onChange={(e) => setData("caption", e.target.value)}
              maxLength={50}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
            {errors?.caption && (
              <p className="text-hpi-red mt-1 text-sm">{errors.caption[0]}</p>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.exclude_from_worksheet}
                onChange={(e) =>
                  setData("exclude_from_worksheet", e.target.checked)
                }
                className="rounded"
              />
              <span className="text-sm">Do not include on Worksheet</span>
            </label>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-6 py-2 text-white disabled:opacity-50"
            >
              {processing ? "Saving..." : "Save"}
            </button>
            <Link
              href={basePath}
              className="text-hpi-active-blue text-sm hover:underline"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
